import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { Middleware } from "redux";

import { formatDateToISOString, isValidDate } from "util/dateUtils";
import { apiCallBegan, ApiCallBeganPayload, apiCallFailed, ApiCallFailedPayload, apiCallSuccess, ApiCallSuccessPayload } from "../api";

const baseURL = window._env_.REACT_APP_BASE_URL || process.env.REACT_APP_BASE_URL;

const knownDateFields = ['StartDate', 'EndDate'];

/**
 * Formats date fields in the payload to ISO 8601 format.
 *
 * @param payload - The API request payload (can be nested).
 * @returns The formatted payload.
 */
export const formatKnownDates = (payload: any): any => {
  if (typeof payload !== 'object' || payload === null) return payload;

  const formatDates = (obj: any) => {
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (knownDateFields.includes(key) && isValidDate(new Date(value))) {
        obj[key] = formatDateToISOString(new Date(value));
      } 
      
      // Handle nested objects and arrays
      else if (typeof value === 'object' && value !== null) {
        formatDates(value);
      }
    });
  };

  formatDates(payload);
  return payload;
};


const api: Middleware =
  ({ dispatch }) =>
  next =>
  async (action: any) => {
    if (action.type !== apiCallBegan.type) {
      return next(action);
    }

    const { url, method, data, onStart, onSuccess, onError, headers = {}, responseType, extraData }: ApiCallBeganPayload = action.payload;

    if (onStart) {
      dispatch({ type: onStart, payload: { extraData } });
    }
    next(action);

    try {
      const formattedData = ["POST", "PUT"].includes(method.toUpperCase()) ? formatKnownDates(data) : data;

      const response = await axios.request({
        baseURL,
        url,
        method,
        data: formattedData,
        headers,
        responseType
      } as AxiosRequestConfig);

      let responseData: any;
      if (responseType === "blob") {
        if (response.status === 204) {
              responseData = null;
          } else {
              responseData = URL.createObjectURL(response.data);
          }
      } else {
        responseData = response.data;
      }

      const payload: ApiCallSuccessPayload = extraData
        ? {
            data: responseData,
            extraData
          }
        : responseData;

      dispatch(apiCallSuccess(payload));

      if (onSuccess) {
        dispatch({
          type: onSuccess,
          payload: extraData
            ? {
                data: responseData,
                extraData
              }
            : responseData
        });
      }
      return response;
    } catch (err) {
      const error = err as AxiosError;

      const errorPayload: ApiCallFailedPayload = {
        code: error?.response?.status ?? 0,
        message:
          typeof error?.response?.data === "string" &&
          error?.response?.data.length <
            parseInt(window._env_.REACT_APP_MAX_ERROR_LENGTH || process.env.REACT_APP_MAX_ERROR_LENGTH || "1000", 10)
            ? error?.response?.data
            : error?.message,
        extraData
      };

      dispatch(apiCallFailed(errorPayload));

      if (onError) {
        dispatch({
          type: onError,
          payload: errorPayload
        });
      }
      return error;
    }
  };

export default api;
